import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DynamicPage, DynamicPageTitle, Title, Bar, Button, Form, FormItem, Label, Input, Table, TableHeaderRow, TableHeaderCell, TableRow, TableCell, CheckBox, MessageStrip, Text, BusyIndicator, FlexBox, ObjectStatus } from '@ui5/webcomponents-react';
import { api, type OpenInvoice, type Rec } from '../api/client';
import { LookupInput } from '../render/LookupInput';
import { useIsMobile } from '../hooks/useIsMobile';

const money = (n: number, c?: string) => `${n.toLocaleString(undefined, { minimumFractionDigits: 2 })}${c ? ' ' + c : ''}`;

/** Create an Incoming (customer) or Outgoing (supplier) payment: pick partner → allocate to open invoices → post. */
export function PaymentNew() {
  const [sp] = useSearchParams();
  const direction = sp.get('direction') === 'outgoing' ? 'outgoing' : 'incoming';
  const isIncoming = direction === 'incoming';
  const nav = useNavigate();
  const isMobile = useIsMobile();

  const [cardCode, setCardCode] = useState('');
  const [cardName, setCardName] = useState('');
  const [account, setAccount] = useState('');
  const [alloc, setAlloc] = useState<Record<number, number>>({});   // docEntry -> amount (present = selected)
  const [msg, setMsg] = useState<{ design: 'Positive' | 'Negative'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const partnerLookup = { entity: 'BusinessPartners', key: 'CardCode', display: 'CardName', filter: `CardType eq '${isIncoming ? 'cCustomer' : 'cSupplier'}'`, copy: { CardName: 'CardName' } };
  const accountLookup = { entity: 'ChartOfAccounts', key: 'AccountCode', searchKey: 'Code', display: 'Name' };

  const invoices = useQuery({ queryKey: ['open-invoices', direction, cardCode], queryFn: () => api.openInvoices(direction, cardCode), enabled: !!cardCode });
  const rows = invoices.data ?? [];

  const total = useMemo(() => Number(Object.values(alloc).reduce((s, v) => s + (v || 0), 0).toFixed(2)), [alloc]);
  const selected = Object.keys(alloc).length;

  const toggle = (inv: OpenInvoice, on: boolean) => setAlloc(a => { const n = { ...a }; if (on) n[inv.DocEntry] = inv.OpenBalance; else delete n[inv.DocEntry]; return n; });
  const setAmt = (docEntry: number, v: number) => setAlloc(a => ({ ...a, [docEntry]: v }));

  const post = async () => {
    if (!cardCode) { setMsg({ design: 'Negative', text: 'Choose a business partner' }); return; }
    if (!account) { setMsg({ design: 'Negative', text: 'Choose a bank/cash G/L account' }); return; }
    const list = Object.entries(alloc).map(([d, v]) => ({ docEntry: Number(d), sumApplied: Number(v) })).filter(i => i.sumApplied > 0);
    if (!list.length) { setMsg({ design: 'Negative', text: 'Select at least one invoice to pay' }); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await api.createPayment(direction, { cardCode, transferAccount: account, invoices: list });
      nav(`/s/${isIncoming ? 'incoming-payments' : 'outgoing-payments'}/${r.DocEntry}`, { replace: true });
    } catch (e) { setMsg({ design: 'Negative', text: (e as Error).message }); } finally { setBusy(false); }
  };

  // Phone: the actions live in a footer bar (Fiori pattern) so the title keeps the full width; desktop keeps them in the title.
  const actions = <>
    <Button onClick={() => nav(-1)}>Cancel</Button>
    <Button design="Emphasized" icon="accept" disabled={busy || !cardCode || total <= 0} onClick={post}>{busy ? 'Posting…' : `Post ${money(total)}`}</Button>
  </>;

  return (
    <DynamicPage titleArea={<DynamicPageTitle heading={<Title level="H3">New {isIncoming ? 'Incoming' : 'Outgoing'} Payment</Title>}
      actionsBar={isMobile ? undefined : <Bar design="Subheader" endContent={actions} />} />}
      showFooter={isMobile} footerArea={isMobile ? <Bar design="Footer" endContent={actions} /> : undefined}>
      {msg && <MessageStrip design={msg.design} onClose={() => setMsg(null)} style={{ marginBottom: '0.5rem' }}>{msg.text}</MessageStrip>}

      <Form layout="S1 M2 L2 XL2" labelSpan="S12 M4 L4 XL4">
        <FormItem labelContent={<Label required>{isIncoming ? 'Customer' : 'Supplier'}</Label>}>
          <LookupInput lookup={partnerLookup} value={cardCode} placeholder={`Search ${isIncoming ? 'customer' : 'supplier'}…`}
            onSelect={(row, key) => { setCardCode(key); setCardName((row?.CardName as string) ?? ''); setAlloc({}); }} />
        </FormItem>
        <FormItem labelContent={<Label required>Bank / cash account</Label>}>
          <LookupInput lookup={accountLookup} value={account} placeholder="Search G/L account…" onSelect={(_row, key) => setAccount(key)} />
        </FormItem>
      </Form>

      {cardName && <Text style={{ display: 'block', margin: '0.5rem 0', color: 'var(--sapNeutralTextColor)' }}>{cardCode} — {cardName}</Text>}

      {cardCode && (invoices.isLoading ? <FlexBox justifyContent="Center" style={{ padding: '2rem' }}><BusyIndicator active /></FlexBox> : (
        <>
          <Title level="H5" style={{ margin: '0.75rem 0 0.25rem' }}>Open invoices ({rows.length})</Title>
          {rows.length === 0 ? <Text>No open invoices for this partner.</Text> : (
            <Table overflowMode="Popin" headerRow={<TableHeaderRow>
              <TableHeaderCell width="3rem" minWidth="3rem" importance={3}><span></span></TableHeaderCell><TableHeaderCell minWidth="5rem" importance={2}><span>No.</span></TableHeaderCell>
              <TableHeaderCell minWidth="7rem"><span>Due</span></TableHeaderCell><TableHeaderCell minWidth="8rem"><span>Total</span></TableHeaderCell>
              <TableHeaderCell minWidth="8rem"><span>Open</span></TableHeaderCell><TableHeaderCell minWidth="9rem" importance={1}><span>Amount to pay</span></TableHeaderCell>
            </TableHeaderRow>}>
              {rows.map(inv => {
                const on = inv.DocEntry in alloc;
                const overdue = inv.DocDueDate && inv.DocDueDate.slice(0, 10) < new Date().toISOString().slice(0, 10);
                return (
                  <TableRow key={inv.DocEntry}>
                    <TableCell><CheckBox checked={on} onChange={e => toggle(inv, e.target.checked)} /></TableCell>
                    <TableCell><span>{inv.DocNum}</span></TableCell>
                    <TableCell><ObjectStatus state={overdue ? 'Negative' : 'None'}>{inv.DocDueDate?.slice(0, 10)}</ObjectStatus></TableCell>
                    <TableCell><span>{money(inv.DocTotal, inv.DocCurrency)}</span></TableCell>
                    <TableCell><span>{money(inv.OpenBalance, inv.DocCurrency)}</span></TableCell>
                    <TableCell>{on
                      ? <Input type="Number" value={String(alloc[inv.DocEntry])} style={{ width: '100%', maxWidth: '9rem', minWidth: '6rem' }} onChange={e => setAmt(inv.DocEntry, Number(e.target.value))} />
                      : <Text style={{ color: 'var(--sapNeutralTextColor)' }}>—</Text>}</TableCell>
                  </TableRow>
                );
              })}
            </Table>
          )}
          {selected > 0 && <Text style={{ display: 'block', marginTop: '0.75rem', fontWeight: 600 }}>Paying {selected} invoice(s) — total {money(total)}</Text>}
        </>
      ))}
    </DynamicPage>
  );
}
